/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useSystemVarsData.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Custom hook for managing System Vars data.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  SystemVarsData,
  SystemVar,
  BulkUpdateSystemVarsPayload,
} from '../types/systemVars.types';
import {
  fetchSystemVars,
  bulkUpdateSystemVars,
  resetToDefaults,
  generateXml,
} from '../api';

/**
 * Options for the useSystemVarsData hook.
 */
interface UseSystemVarsDataOptions {
  /** Callback fired upon successful load or save, receiving a success message. */
  onSuccess?: (message: string) => void;
  /** Callback fired upon failure, receiving the Error object. */
  onError?: (error: Error) => void;
}

/**
 * Custom React hook for managing System Vars data.
 * Handles fetching dynamic settings from the API, bulk updating, checking for modifications,
 * and generating the FreeSWITCH vars.xml preview.
 * @param options - Configuration options for the hook.
 */

export function useSystemVarsData(options: UseSystemVarsDataOptions = {}) {
  const { onSuccess, onError } = options;

  // Use refs to store callbacks to prevent infinite loops
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const [data, setData] = useState<SystemVarsData>({ settings: {}, categories: [] });
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [originalData, setOriginalData] = useState<Record<string, string>>({});
  const [defaultValues, setDefaultValues] = useState<Record<string, string>>({});
  const [xmlPreview, setXmlPreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Create setting lookup by variable name
  const settingByVariableName = useMemo(() => {
    return Object.values(data.settings)
      .flat()
      .reduce<Record<string, SystemVar>>((acc, setting) => {
        if (setting && typeof setting === 'object' && 'settingMetadata' in setting) {
          const systemVar = setting as SystemVar;
          if (systemVar.settingMetadata?.variable_name) {
            acc[systemVar.settingMetadata.variable_name] = systemVar;
          }
        }
        return acc;
      }, {});
  }, [data.settings]);

  // Check if there are unsaved changes
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  // Load system vars
  const loadSystemVars = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await fetchSystemVars();

      if (result.success && result.data) {
        setData(result.data);

        // Initialize form data with current values and original values
        const newFormData: Record<string, string> = {};
        const newDefaultValues: Record<string, string> = {};
        Object.values(result.data.settings).flat().forEach((setting: SystemVar) => {
          newFormData[setting.settingMetadata.variable_name] = setting.settingMetadata.value;
          // Use original_value from database if available, otherwise fallback to default_value
          newDefaultValues[setting.settingMetadata.variable_name] =
            setting.originalValue?.value || setting.settingMetadata.default_value;
        });

        setFormData(newFormData);
        setOriginalData({ ...newFormData });
        setDefaultValues(newDefaultValues);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to load system vars');
      setError(error);
      onErrorRef.current?.(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save changes
  const saveChanges = useCallback(
    async (): Promise<void> => {

      try {
        setIsSaving(true);
        setError(null);

        // Prepare data for bulk update
        const settingsToUpdate = Object.entries(formData).map(([variableName, value]) => {
          const setting = settingByVariableName[variableName];

          if (!setting) {
            throw new Error(`Setting not found for variable: ${variableName}`);
          }

          return {
            id: setting.id,
            settingMetadata: {
              ...setting.settingMetadata,
              value,
            },
          };
        });

        const payload: BulkUpdateSystemVarsPayload = {
          settings: settingsToUpdate,
        };

        const result = await bulkUpdateSystemVars(payload);

        if (result.success) {
          setOriginalData({ ...formData });
          await loadSystemVars(); // Reload to get updated data
          onSuccessRef.current?.(result.message || 'System variables updated successfully');
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to save system vars');
        setError(error);
        onErrorRef.current?.(error);
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [formData, hasChanges, settingByVariableName, loadSystemVars]
  );

  // Reset to defaults (server call)
  const resetToDefaultsAction = useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);

      const result = await resetToDefaults();

      if (result.success) {
        await loadSystemVars();
        onSuccessRef.current?.(result.message || 'System variables reset to defaults successfully');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to reset to defaults');
      setError(error);
      onErrorRef.current?.(error);
    } finally {
      setIsSaving(false);
    }
  }, [loadSystemVars]);

  // Generate XML preview
  const generateXmlPreview = useCallback(async () => {
    try {
      setError(null);

      const result = await generateXml();

      if (result.success && result.data) {
        setXmlPreview(result.data.xml);
        onSuccessRef.current?.('XML preview generated successfully');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to generate XML');
      setError(error);
      onErrorRef.current?.(error);
    }
  }, []);

  // Handle field changes
  const handleChange = useCallback((variableName: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [variableName]: value,
    }));
  }, []);

  // Check if field is modified from default
  const isModifiedFromDefault = useCallback(
    (variableName: string) => formData[variableName] !== defaultValues[variableName],
    [defaultValues, formData]
  );

  // Reset field to default
  const handleResetFieldToDefault = useCallback(
    (variableName: string) => {
      if (defaultValues[variableName] !== undefined) {
        handleChange(variableName, defaultValues[variableName]);
      }
    },
    [defaultValues, handleChange]
  );

  // Reset to original values (local only)
  const resetToOriginal = useCallback(() => {
    setFormData({ ...originalData });
  }, [originalData]);

  // Load data on mount (only once)
  useEffect(() => {
    loadSystemVars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    formData,
    originalData,
    defaultValues,
    xmlPreview,
    isLoading,
    isSaving,
    error,
    hasChanges,
    settingByVariableName,
    loadSystemVars,
    saveChanges,
    resetToDefaultsAction,
    generateXmlPreview,
    handleChange,
    isModifiedFromDefault,
    handleResetFieldToDefault,
    resetToOriginal,
    setXmlPreview,
  };
}

